import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { AnalystNumberField } from '@/lib/analyst-reading';
import {
  ANALYST_NUMBER_BOUNDS,
  analystNumberOf,
  analystNumberRefusal,
  styleWeightHint,
} from '@/lib/analyst-reading';
import type { ProtocolAnalyst } from '@matter_extractor/shared';
import { useId, useState } from 'react';

/**
 * Le groupe « Certitude et poids » de la section Analyste (analyste.md §3.2, K2 ;
 * planche `AnalysteA`) : les trois nombres que le protocole règle - le seuil d'une
 * paire (70), le seuil d'un objectif seul (85) et le poids du style (50 %) -, trois
 * champs numériques en ligne, chacun avec son unité et sa phrase.
 *
 * ## The draft only ever holds a number the schema accepts
 *
 * A threshold is a whole number from 51 to 100 and the weight one from 0 to 100
 * (`ANALYST_NUMBER_BOUNDS`), and the autosave sends the draft whole: a 7 typed on
 * the way to 70 would be a 400, the one answer an autosave must never get. So the
 * field keeps WHAT IS TYPED in its own state and writes the draft only when the text
 * reads as a whole number inside the bounds - the régime of the euro fields of
 * `model-panel.tsx`, where a candidate moves on a value that parses. Clamping each
 * keystroke, as the threshold of the Noyau block does on its 0 to 100, would turn
 * the 7 of 70 into 51 and the next 0 into 510. A text that does not read is said
 * under its field, in an `<output>` that stays mounted (role `status`) and the draft
 * keeps its last good value; leaving the field puts that value back.
 *
 * ## Why `type="number"` anyway
 *
 * The maquette draws it, the spinner steps the weight by 5 (the maquette's `step`),
 * and a whole number has none of the comma that sinks a decimal field. The step is a
 * stride and not a rule: any whole number from 0 to 100 is a weight.
 *
 * The line under the weight is written from the weight itself - « Note d'un
 * candidat : 50 % de compatibilité, 50 % de style. » (§8.1) - so the field and the
 * sentence cannot say two things.
 */

export interface AnalystSettingsProps {
  readonly analyst: ProtocolAnalyst;
  /** « Voir », or a retired protocol: nothing is settable. */
  readonly readOnly: boolean;
  readonly onChange: (next: ProtocolAnalyst) => void;
}

interface FieldSpec {
  readonly field: AnalystNumberField;
  readonly label: string;
  readonly unit: string;
  readonly step: number;
  readonly testId: string;
}

const FIELDS: readonly FieldSpec[] = [
  {
    field: 'pairThreshold',
    label: 'Seuil d’une paire',
    unit: 'sur 100',
    step: 1,
    testId: PROTOCOL_TEST_IDS.analystPairThreshold,
  },
  {
    field: 'soloThreshold',
    label: 'Seuil d’un objectif seul',
    unit: 'sur 100',
    step: 1,
    testId: PROTOCOL_TEST_IDS.analystSoloThreshold,
  },
  {
    field: 'styleWeight',
    label: 'Poids du style',
    unit: '%',
    step: 5,
    testId: PROTOCOL_TEST_IDS.analystStyleWeight,
  },
];

/** The phrase under each number: what it sets, in the words of the maquette. */
function helpOf(field: AnalystNumberField, analyst: ProtocolAnalyst): string {
  switch (field) {
    case 'pairThreshold':
      return 'Les objectifs d’un niveau l’atteignent tous sur la même fonction.';
    case 'soloThreshold':
      return 'Quand un niveau n’a qu’un objectif lisible.';
    case 'styleWeight':
      return styleWeightHint(analyst.styleWeight);
  }
}

export function AnalystSettings({ analyst, readOnly, onChange }: AnalystSettingsProps) {
  const helpId = useId();

  return (
    <fieldset
      data-testid={PROTOCOL_TEST_IDS.analystSettings}
      aria-describedby={helpId}
      className="flex min-w-0 flex-col border-0 p-0"
    >
      <legend className="font-semibold text-gray-800 text-sm">Certitude et poids</legend>
      <p id={helpId} className="mt-0.5 text-gray-600 text-xs leading-4">
        Quand le code tient un niveau pour certain, et la part du style dans la note d’un candidat.
      </p>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        {FIELDS.map((spec) => (
          <NumberField
            key={spec.field}
            spec={spec}
            value={analyst[spec.field]}
            help={helpOf(spec.field, analyst)}
            readOnly={readOnly}
            onValue={(value) => onChange({ ...analyst, [spec.field]: value })}
          />
        ))}
      </div>
    </fieldset>
  );
}

interface NumberFieldProps {
  readonly spec: FieldSpec;
  /** What the draft holds. */
  readonly value: number;
  readonly help: string;
  readonly readOnly: boolean;
  /** Told only a number that reads and sits inside the bounds. */
  readonly onValue: (value: number) => void;
}

function NumberField({ spec, value, help, readOnly, onValue }: NumberFieldProps) {
  const inputId = useId();
  const unitId = useId();
  const helpId = useId();
  const refusalId = useId();
  /** What is being typed, or null while the field follows the draft. */
  const [typed, setTyped] = useState<string | null>(null);
  const { min, max } = ANALYST_NUMBER_BOUNDS[spec.field];
  const refused = typed !== null && analystNumberOf(spec.field, typed) === null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={inputId} className="font-semibold text-gray-800 text-sm">
        {spec.label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="number"
          inputMode="numeric"
          data-testid={spec.testId}
          value={typed ?? value}
          readOnly={readOnly}
          min={min}
          max={max}
          step={spec.step}
          aria-invalid={refused}
          aria-describedby={`${unitId} ${helpId} ${refusalId}`}
          onChange={(event) => {
            const text = event.currentTarget.value;
            const parsed = analystNumberOf(spec.field, text);
            if (parsed === null) {
              setTyped(text);
              return;
            }
            setTyped(null);
            onValue(parsed);
          }}
          onBlur={() => setTyped(null)}
          className="h-11 w-21 rounded-md border-2 border-gray-300 bg-white pr-2 pl-3 text-base read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 aria-invalid:border-rouge"
        />
        <span id={unitId} className="text-gray-700 text-sm">
          {spec.unit}
        </span>
      </div>
      <div className="flex flex-col">
        <p id={helpId} className="text-gray-600 text-xs leading-4">
          {help}
        </p>
        {/* An `<output>` (role `status`), mounted and EMPTY until a text does not read: a
            live region that appears with its text is one some screen readers never announce. */}
        <output
          id={refusalId}
          data-testid={PROTOCOL_TEST_IDS.analystNumberRefusal}
          data-field={spec.field}
          className="block font-semibold text-rouge-deep text-xs leading-4"
        >
          {refused ? analystNumberRefusal(spec.field) : ''}
        </output>
      </div>
    </div>
  );
}
